<!-- vto-pwa/src/lib/capture/LiveMirror.svelte -->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { startCameraPreview, stopCameraPreview, captureFromVideo, type CaptureResult } from './capture';
  import { LiveTracker } from '../tracking/liveTracker';
  import type { LipDetection } from '../tracking/faceLandmarker';
  import type { VtoCopy } from '../i18n/vtoCopy';
  import {
    brandLabel,
    matchQuality,
    type CrossBrandComponentId,
    type CrossBrandMatchItem,
    type MatchQuality
  } from '../catalog/crossBrandMatches';

  type CrossBrandTeaserEntry = { componentId: CrossBrandComponentId; item: CrossBrandMatchItem };

  let {
    copy,
    oncapture,
    onusephoto,
    crossBrand = []
  }: {
    copy: VtoCopy;
    oncapture: (r: CaptureResult) => void;
    onusephoto: () => void;
    crossBrand?: CrossBrandTeaserEntry[];
  } = $props();

  // Badge colour per perceptual match tier — keep in sync with App.svelte.
  const MATCH_QUALITY_CLASS: Record<MatchQuality, string> = {
    perfect: 'bg-emerald-100 text-emerald-700',
    excellent: 'bg-teal-100 text-teal-700',
    good: 'bg-amber-100 text-amber-700',
    approximate: 'bg-brand-bg text-brand-muted'
  };

  let videoEl: HTMLVideoElement | undefined = $state();
  let overlayEl: HTMLCanvasElement | undefined = $state();
  let stream: MediaStream | null = null;
  let tracker: LiveTracker | null = null;
  let raf = 0;
  let hasFace = $state(false);
  let frames = $state(0); // exposed for tests via data attribute
  let cameraStarted = $state(false);
  let cameraStarting = $state(false);
  let cameraError: 'denied' | 'error' | null = $state(null);
  let destroyed = false;

  function drawOverlay(det: LipDetection | null) {
    const ctx = overlayEl?.getContext('2d');
    if (!ctx || !overlayEl) return;
    const vw = videoEl?.videoWidth ?? 0;
    const vh = videoEl?.videoHeight ?? 0;
    if (overlayEl.width !== vw) overlayEl.width = vw;
    if (overlayEl.height !== vh) overlayEl.height = vh;
    ctx.clearRect(0, 0, overlayEl.width, overlayEl.height);
    if (!det) return;
    ctx.fillStyle = 'rgba(214,166,150,0.9)';
    for (const p of det.landmarks) {
      ctx.beginPath();
      ctx.arc(p.x * overlayEl.width, p.y * overlayEl.height, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  async function loop() {
    if (!tracker || !videoEl?.videoWidth) {
      raf = requestAnimationFrame(loop);
      return;
    }
    const det = await tracker.detect(videoEl, performance.now());
    if (destroyed) return;
    hasFace = det !== null;
    frames += 1;
    drawOverlay(det);
    raf = requestAnimationFrame(loop);
  }

  async function startMirror() {
    cameraStarted = true;
    cameraStarting = true;
    cameraError = null;
    try {
      if (videoEl) stream = await startCameraPreview(videoEl);
      raf = requestAnimationFrame(loop);
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        cameraError = 'denied';
      } else {
        cameraError = 'error';
      }
    } finally {
      cameraStarting = false;
    }
  }

  async function onCaptureClick() {
    if (!videoEl) return;
    // captureFromVideo mirrors AND caps to MAX_DECODE_SIDE (1280px) — the same
    // memory envelope the photo flow assumes. Uncapped 1920x1440 frames blew
    // past the iOS WKWebView canvas budget during the look composite.
    const result = await captureFromVideo(videoEl);
    oncapture(result);
  }

  function onVisibility() {
    if (document.hidden) {
      cancelAnimationFrame(raf);
      raf = 0;
      stopCameraPreview(stream);
      stream = null;
    } else {
      if (!destroyed && cameraStarted && !cameraError && !stream) {
        startMirror();
      }
    }
  }

  onMount(() => {
    tracker = new LiveTracker();
    document.addEventListener('visibilitychange', onVisibility);
    // No auto-start: the camera is gated behind the neutral pre-prompt button
    // (copy.capture.camera.enable) — Apple 5.1.1(iv), same gate as the photo
    // flow in Capture.svelte. Bypassing it caused rejection build 4.
  });

  onDestroy(() => {
    destroyed = true;
    cancelAnimationFrame(raf);
    document.removeEventListener('visibilitychange', onVisibility);
    stopCameraPreview(stream);
    tracker?.dispose();
  });
</script>

<section class="relative flex flex-col gap-3" data-testid="live-mirror" data-frames={frames}>
  <h2 class="font-display text-lg">{copy.mirror.title}</h2>
  <div class="relative overflow-hidden rounded-lg">
    <!-- mirror the video horizontally to match a real mirror -->
    <!-- svelte-ignore a11y_media_has_caption -->
    <video bind:this={videoEl} class="w-full -scale-x-100" playsinline muted autoplay></video>
    <canvas bind:this={overlayEl} class="pointer-events-none absolute inset-0 h-full w-full -scale-x-100"></canvas>
    {#if cameraError}
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 p-4 text-center text-white" data-testid="mirror-camera-error">
        <p class="text-sm font-semibold">
          {cameraError === 'denied' ? copy.capture.camera.deniedTitle : copy.capture.errors.cameraUnavailable}
        </p>
        {#if cameraError === 'denied'}
          <p class="text-xs opacity-80">{copy.capture.camera.deniedBody}</p>
        {/if}
        <button
          type="button"
          class="min-h-[44px] rounded-pill bg-brand-accent px-4 py-2 text-sm font-semibold text-white"
          onclick={startMirror}
          data-testid="mirror-camera-retry"
        >
          {copy.common.retry}
        </button>
        <button
          type="button"
          class="text-sm text-white underline underline-offset-2"
          onclick={onusephoto}
          data-testid="mirror-use-photo"
        >
          {copy.capture.camera.upload}
        </button>
      </div>
    {:else if !cameraStarted}
      <!-- Neutral pre-prompt: explain before the OS permission dialog. -->
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 p-4 text-center text-white" data-testid="mirror-camera-preprompt">
        <p class="text-xs opacity-80">{copy.capture.camera.privacy}</p>
        <button
          type="button"
          class="btn-primary min-h-[44px]"
          onclick={startMirror}
          data-testid="mirror-camera-enable"
        >
          {copy.capture.camera.enable}
        </button>
      </div>
    {:else if cameraStarting && !stream}
      <div class="absolute inset-0 flex items-center justify-center bg-black/60 p-4 text-center text-white" data-testid="mirror-camera-starting">
        <p class="text-sm">{copy.capture.camera.starting}</p>
      </div>
    {:else if !hasFace}
      <p class="absolute inset-x-0 bottom-2 text-center text-[12px] text-white drop-shadow">{copy.mirror.searching}</p>
    {/if}
  </div>
  <p class="text-[12px] text-brand-muted">{copy.mirror.hint}</p>

  {#if crossBrand.length > 0}
    <!-- Live cross-brand teaser: the distinctive "this look across brands" hook,
         visible before capture. Best (closest-ΔE) match per component. -->
    <div class="rounded-lg border border-brand-highlight bg-white/85 p-2" data-testid="mirror-crossbrand-teaser">
      <p class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">{copy.crossBrand.title}</p>
      <div class="mt-1 flex flex-col gap-1">
        {#each crossBrand as entry (entry.componentId)}
          {@const q = matchQuality(entry.item)}
          <div class="flex items-center gap-2">
            <span class="h-4 w-4 shrink-0 rounded-full border border-black/10" style="background-color: {entry.item.hex};" aria-hidden="true"></span>
            <span class="shrink-0 text-[11px] text-brand-muted">{copy.components[entry.componentId]}</span>
            <span class="truncate text-[11px] font-semibold text-brand-ink">{brandLabel(entry.item.brand)} · {entry.item.shade_name}</span>
            <span class="ml-auto shrink-0 rounded-pill px-1.5 py-0.5 text-[9px] font-semibold {MATCH_QUALITY_CLASS[q]}">{copy.crossBrand.quality[q]}</span>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <button
    type="button"
    class="min-h-[44px] rounded-pill bg-brand-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
    onclick={onCaptureClick}
    disabled={!hasFace}
    data-testid="mirror-capture"
  >
    {copy.mirror.capture}
  </button>
  <button
    type="button"
    class="inline-flex min-h-[44px] items-center justify-center text-sm text-brand-muted underline underline-offset-2"
    onclick={onusephoto}
    data-testid="mirror-use-photo"
  >
    {copy.capture.camera.upload}
  </button>
</section>
